import { WifiOff, RotateCw, AlertTriangle } from "lucide-react";
import React, { Component, ErrorInfo, ReactNode } from "react";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Global Error Boundary
 * Catch unhandled exceptions in the component tree.
 * Specifically designed to handle "ChunkLoadError" which happens when a new deployment occurs
 * and the user acts on a stale tab, trying to fetch old JS files that no longer exist.
 */
class GlobalErrorBoundary extends Component<Props, State> {
  public state: State = { hasError: false, error: null };
  public readonly props: Readonly<Props>;

  constructor(props: Props) {
    super(props);
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("GlobalErrorBoundary caught an error:", error, errorInfo);

    // Check for Chunk Load Errors (Stale Deployments)
    const message = error.message?.toLowerCase() || "";
    const isChunkError =
      message.includes("dynamically imported module") ||
      message.includes("importing a module script failed") ||
      message.includes("error loading dynamically imported module") ||
      message.includes("failed to fetch dynamically imported module") ||
      error.name === "ChunkLoadError";

    if (isChunkError) {
      console.log("Detected ChunkLoadError. Attempting auto-reload...");

      // Prevent infinite reload loops
      const lastReload = sessionStorage.getItem("last_chunk_reload");
      const now = Date.now();

      if (!lastReload || now - parseInt(lastReload) > 10000) {
        sessionStorage.setItem("last_chunk_reload", now.toString());
        window.location.reload();
        return;
      }
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // If we have a custom fallback (not used currently but good for flexibility)
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default Error UI - Styled to match App theme (Dark/Modern)
      return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-[#09090b] text-white p-6 text-center animate-fade-in">
          <div className="relative mb-8">
            <div className="absolute inset-0 bg-red-500/20 blur-3xl rounded-full" />
            <div className="relative z-10 w-24 h-24 rounded-2xl bg-[#18181b] border border-white/10 flex items-center justify-center shadow-2xl">
              <AlertTriangle size={40} className="text-red-500" />
            </div>
            <div className="absolute -top-2 -right-2 bg-[#27272a] p-2 rounded-full">
              <WifiOff size={16} className="text-white/50" />
            </div>
          </div>

          <h1 className="text-3xl font-bold mb-3 tracking-tight">
            Signal Interrupted
          </h1>

          <p className="text-white/60 max-w-md mb-8 leading-relaxed">
            We encountered an unexpected error. This might be due to a new
            update or a temporary glitch in the connection.
          </p>

          <div className="bg-[#18181b] rounded-lg p-4 mb-8 max-w-md w-full border border-white/5 text-left overflow-hidden">
            <div className="text-xs uppercase tracking-wider text-white/30 font-semibold mb-2">
              Error Details
            </div>
            <code className="text-xs text-red-300 font-mono break-all block">
              {this.state.error?.message || "Unknown Error"}
            </code>
          </div>

          <button
            onClick={this.handleReload}
            className="group px-6 py-3 bg-white text-black font-bold rounded-xl hover:scale-105 hover:shadow-lg hover:shadow-white/10 transition-all flex items-center gap-2"
          >
            <RotateCw
              size={18}
              className="group-hover:rotate-180 transition-transform duration-500"
            />
            Reload Application
          </button>

          <p className="mt-8 text-xs text-white/30">
            Error Code: 500_CLIENT_CRASH
          </p>
        </div>
      );
    }

    return this.props.children;
  }
}

export default GlobalErrorBoundary;
